import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Activity, AlignJustify, ArrowLeftRight, BarChart2, BarChart3, Bell,
  BookOpen, Bot, Briefcase, Building, Building2, Calendar, ChevronRight,
  ClipboardCheck, ClipboardList, Cpu, Database, DollarSign, FileBarChart2,
  FilePlus, FileSearch, FileText, FileUp, FlaskConical, Gauge, Gem, GitCompare,
  GitMerge, Globe, Globe2, Home, Layers, LayoutDashboard, Library, LineChart,
  Lock, MapPin, Menu, PanelLeftClose, PanelLeftOpen, Percent, Receipt, Scale,
  ScrollText, Search, Settings, Shield, Sparkles, Table2, TrendingDown,
  TrendingUp, Upload, Users, Workflow,
} from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
import './AppShell.css';

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL?.trim() || 'http://localhost:8010').replace(/\/$/, '');

const PACKAGE_LABELS = {
  CORE: 'Core',
  GROWTH: 'Growth',
  ENTERPRISE: 'Enterprise',
};

const APP_LOGO_SRC = `${process.env.PUBLIC_URL || ''}/Rationexus-Logo.png`;
const SIDEBAR_COLLAPSED_KEY = 'rn.sidebar.collapsed';
const MOBILE_BREAKPOINT = 768;
const DESKTOP_BREAKPOINT = 1280;

const managementItems = [
  { label: 'Kullanıcılar', path: '/users', icon: Users },
  { label: 'Roller', path: '/roles', icon: Shield },
  { label: 'Yetkiler', path: '/permissions', icon: Lock },
  { label: 'Bölgeler', path: '/regions', icon: MapPin },
  { label: 'Sektör Grupları', path: '/sector-groups', icon: Layers },
  { label: 'Sektörler', path: '/sectors', icon: Building2 },
  { label: 'Rasyolar', path: '/system/ratios', icon: Percent },
  { label: 'Standart Rasyolar', path: '/system/standard-ratios', icon: BookOpen },
  { label: 'Sektör Rasyoları', path: '/sector-ratios', icon: BarChart3 },
  { label: 'Şirketler', path: '/companies', icon: Briefcase },
  { label: 'Dönemler', path: '/periods', icon: Calendar },
  { label: 'AI Kullanımları ve Maliyetleri', path: '/system/ai-usage-costs', icon: Cpu },
  { label: 'IFRS Standartları', path: '/system/ifrs-standards', icon: FileText }
];

const dataEntryItems = [
  { label: 'TDHP Oluşturma', path: '/data-ingestion/tdhp-plan', icon: FilePlus },
  { label: 'Mizan Aktarım', path: '/data-ingestion/mizan-import', icon: FileUp },
  { label: 'Muhasebe Fişleri Aktarım', path: '/data-ingestion/fis-import', icon: Receipt }
];

const transferredDataItems = [
  { label: 'Geçici Mizan', path: '/transferred-data/draft-trial-balance', icon: ClipboardList },
  { label: 'Muhasebe Fişleri', path: '/transferred-data/accounting-vouchers', icon: ScrollText }
];

const financialStatementsItems = [
  { label: 'Kebir Mizan', path: '/financial-statements/general-ledger-trial-balance', icon: AlignJustify },
  { label: 'Gelir Tablosu', path: '/financial-statements/income-statement', icon: TrendingUp },
  { label: 'Bilanço', path: '/financial-statements/balance-sheet', icon: Scale },
];

const ifrsFinancialStatementsItems = [
  { label: 'IFRS Uyum Çalışma Sayfası', path: '/financial-statements/ifrs-worksheet', icon: ClipboardCheck },
  { label: 'Finansal Durum Tablosu', path: '/financial-statements/ifrs-reports/financial-position', icon: BarChart2 },
  { label: 'Kar veya Zarar + OCI', path: '/financial-statements/ifrs-reports/profit-loss-oci', icon: TrendingUp },
  { label: 'Nakit Akisi Tablosu', path: '/financial-statements/ifrs-reports/cash-flow', icon: Workflow },
  { label: 'Ozkaynak Degisim Tablosu', path: '/financial-statements/ifrs-reports/equity-changes', icon: GitMerge },
  { label: 'Dipnotlar', path: '/financial-statements/ifrs-reports/notes', icon: FileText },
  { label: 'VUK-IFRS Karsilastirma Raporu', path: '/financial-statements/ifrs-reports/vuk-ifrs-comparison', icon: GitCompare }
];

const financialAnalysisItems = [
  { label: 'Ters Bakiye Kontrolü ve Düzeltmeler', path: '/financial-analysis/reverse-balance', icon: ArrowLeftRight },
  { label: 'Gelir Tablosu-Dönem Sonu Yansıtma İşlemleri', path: '/financial-analysis/pnl-closing', icon: TrendingDown },
  { label: 'Bilanço Dönem Sonu Kapanış İşlemleri', path: '/financial-analysis/balance-closing', icon: Scale }
];

const reportItems = [
  { label: 'Hesap Ekstresi', path: '/reports/account-statement', icon: FileSearch },
  { label: 'Aylık Gelir-Gider Raporu', path: '/reports/monthly-income-expense', icon: BarChart2 }
];

const analysisItems = [
  { label: 'Şirket Rasyoları', path: '/analysis/ratios-standard', icon: Percent },
  { label: 'Rasyo Trend Analizi', path: '/analysis/ratio-trend', icon: TrendingUp },
  { label: 'Sektörel Rasyo Analizi', path: '/analysis/sector-ratio', icon: Building },
  { label: 'Finansal Analiz Dashboard', path: '/analysis/dashboard', icon: LayoutDashboard },
];

const ratioNexusAiItems = [
  { label: 'AI Finansal Analiz Raporu', path: '/analysis/ai-report', icon: Bot },
  { label: 'ScenarioLab - Finansal Etki Simulasyonu', path: '/analysis/stress-test', icon: FlaskConical },
];

const cfoPanelItems = [
  { label: 'TÜFE', path: '/makro-veriler/tufe', icon: Activity },
  { label: 'Faiz', path: '/makro-veriler/faiz', icon: Percent },
  { label: 'Döviz', path: '/makro-veriler/doviz', icon: DollarSign },
  { label: 'Altın', path: '/makro-veriler/altin', icon: Gem },
  { label: 'Kapasite Kullanım', path: '/makro-veriler/kapasite-kullanim', icon: Gauge },
  { label: 'Sektör Bilançoları', path: '/makro-veriler/sektor-bilancolari', icon: Library }
];

const navItems = [
  { label: 'Ana Sayfa', path: '/home', icon: Home },
  { label: 'Makro Veriler', icon: Globe2, children: cfoPanelItems },
  { label: 'Dashboard', path: '/dashboard', icon: LayoutDashboard },
  { label: 'Veri Girişi', icon: Upload, children: dataEntryItems },
  { label: 'Aktarılan Veriler', icon: Database, children: transferredDataItems },
  { label: 'Dönem Sonu Kontrol ve Düzeltmeler', icon: GitMerge, children: financialAnalysisItems },
  { label: 'Mali Tablolar', icon: Table2, children: financialStatementsItems },
  { label: 'IFRS-Mali Tablolar', icon: Globe, children: ifrsFinancialStatementsItems },
  { label: 'Raporlar', icon: FileBarChart2, children: reportItems },
  { label: 'Finansal Analiz', icon: LineChart, children: analysisItems },
  { label: 'RatioNexus AI', icon: Sparkles, aiGroup: true, children: ratioNexusAiItems },
  { label: 'Sistem Yönetimi', icon: Settings, children: managementItems },
];

const UI_TEXT = {
  'tr-TR': {
    brand: 'RatioNexus',
    menuSearchPlaceholder: 'Menude ara (ornek: hesap)',
    logout: 'Cikis',
    topbarUserAria: 'Giris yapan kullanici',
    user: 'Kullanici',
    language: 'Dil',
    turkish: 'Turkce',
    english: 'English'
  },
  'en-US': {
    brand: 'RatioNexus',
    menuSearchPlaceholder: 'Search menu (e.g. account)',
    logout: 'Logout',
    topbarUserAria: 'Signed in user',
    user: 'User',
    language: 'Language',
    turkish: 'Turkish',
    english: 'English'
  }
};

const MENU_TR_TO_EN = {
  'Ana Sayfa': 'Home',
  'Makro Veriler': 'Macro Data',
  Dashboard: 'Dashboard',
  'Sistem Yönetimi': 'System Management',
  'Veri Girişi': 'Data Entry',
  'Aktarılan Veriler': 'Transferred Data',
  'Dönem Sonu Kontrol ve Düzeltmeler': 'Period-End Controls and Adjustments',
  'Mali Tablolar': 'Financial Statements',
  'IFRS-Mali Tablolar': 'IFRS-Financial Statements',
  Raporlar: 'Reports',
  'Finansal Analiz': 'Financial Analysis',
  Kullanıcılar: 'Users',
  Roller: 'Roles',
  Yetkiler: 'Permissions',
  Bölgeler: 'Regions',
  'Sektör Grupları': 'Sector Groups',
  Sektörler: 'Sectors',
  Rasyolar: 'Ratios',
  'Standart Rasyolar': 'Standard Ratios',
  'Sektör Rasyoları': 'Sector Ratios',
  Şirketler: 'Companies',
  Dönemler: 'Periods',
  'AI Kullanımları ve Maliyetleri': 'AI Usage and Costs',
  'IFRS Standartları': 'IFRS Standards',
  'TDHP Oluşturma': 'TDHP Creation',
  'Mizan Aktarım': 'Trial Balance Import',
  'Muhasebe Fişleri Aktarım': 'Voucher Import',
  'Geçici Mizan': 'Draft Trial Balance',
  'Muhasebe Fişleri': 'Accounting Vouchers',
  'Kebir Mizan': 'General Ledger Trial Balance',
  'Gelir Tablosu': 'Income Statement',
  Bilanço: 'Balance Sheet',
  'Finansal Durum Tablosu': 'Financial Position Statement',
  'Kar veya Zarar + OCI': 'Profit or Loss + OCI',
  'Nakit Akisi Tablosu': 'Cash Flow Statement',
  'Ozkaynak Degisim Tablosu': 'Statement of Changes in Equity',
  Dipnotlar: 'Notes',
  'VUK-IFRS Karsilastirma Raporu': 'VUK-IFRS Comparison Report',
  'IFRS Donusum ve Raporlama': 'IFRS Transformation and Reporting',
  'IFRS Uyum Çalışma Sayfası': 'IFRS Compliance Worksheet',
  'VUK-IFRS Dönüşüm Sihirbazı': 'VUK-IFRS Transformation Wizard',
  'Ters Bakiye Kontrolü ve Düzeltmeler': 'Reverse Balance Control and Fixes',
  'Gelir Tablosu-Dönem Sonu Yansıtma İşlemleri': 'Income Statement Period-End Transfer Entries',
  'Bilanço Dönem Sonu Kapanış İşlemleri': 'Balance Sheet Period-End Closing',
  'Hesap Ekstresi': 'Account Statement',
  'Aylık Gelir-Gider Raporu': 'Monthly Income-Expense Report',
  'Şirket Rasyoları': 'Company Ratios',
  'Rasyo Trend Analizi': 'Ratio Trend Analysis',
  'Sektörel Rasyo Analizi': 'Sector Ratio Analysis',
  'AI Finansal Analiz Raporu': 'AI Financial Analysis Report',
  'Finansal Analiz Dashboard': 'Financial Analysis Dashboard',
  'ScenarioLab - Finansal Etki Simulasyonu': 'ScenarioLab - Financial Impact Simulation',
  'RatioNexus AI': 'RatioNexus AI',
  TÜFE: 'CPI',
  Faiz: 'Interest',
  Döviz: 'FX',
  Altın: 'Gold',
  'Kapasite Kullanım': 'Capacity Utilization',
  'Sektör Bilançoları': 'Sector Balance Sheets'
};

const normalizeRoleName = (rawRole) => {
  const raw = (rawRole || '').trim();
  if (!raw) {
    return '';
  }
  const canonical = raw.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
  if (canonical === 'SYSADMIN') {
    return 'SYS_ADMIN';
  }
  if (canonical === 'TENANTADMIN') {
    return 'TENANT_ADMIN';
  }
  if (canonical === 'TENANTUSER') {
    return 'TENANT_USER';
  }
  return raw.toUpperCase();
};

const resolveRoleFromStorage = () => {
  const storedRole = normalizeRoleName(sessionStorage.getItem('authRole'));
  if (storedRole) {
    return storedRole;
  }
  const token = sessionStorage.getItem('authToken');
  if (!token || !token.includes('.')) {
    return '';
  }
  try {
    const payload = token.split('.')[1];
    const decoded = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
    return normalizeRoleName(decoded?.role);
  } catch (_error) {
    return '';
  }
};

const isMenuItemActive = (item, location) => {
  if (typeof item.isActive === 'function') {
    return item.isActive(location);
  }
  if (!item.path) {
    return false;
  }
  const pathname = window.location.pathname || location.pathname;
  return pathname.startsWith(item.path);
};

const resolveBreadcrumb = (location, items) => {
  for (const item of items) {
    if (item.path && location.pathname.startsWith(item.path) && item.path !== '/') {
      return { group: null, page: item.label };
    }
    if (item.children) {
      const match = item.children.find((child) => location.pathname.startsWith(child.path));
      if (match) {
        return { group: item.label, page: match.label };
      }
    }
  }
  return null;
};

function AppShell() {
  const navigate = useNavigate();
  const location = useLocation();
  const { language, setLanguage } = useLanguage();
  const t = (key) => UI_TEXT[language]?.[key] || UI_TEXT['tr-TR'][key] || key;
  const menuLabel = useCallback(
    (label) => (language.startsWith('en') ? (MENU_TR_TO_EN[label] || label) : label),
    [language]
  );
  const authRole = resolveRoleFromStorage();
  const authUsername = (sessionStorage.getItem('authUsername') || '').trim();
  const isTenantUser = authRole === 'TENANT_USER';
  const isSysAdmin = authRole === 'SYS_ADMIN';

  const getViewportWidth = () => (typeof window === 'undefined' ? DESKTOP_BREAKPOINT : window.innerWidth);
  const resolveInitialCollapsed = () => {
    if (typeof window === 'undefined') {
      return false;
    }
    const width = window.innerWidth;
    const stored = window.localStorage.getItem(SIDEBAR_COLLAPSED_KEY);
    if (stored === '1' || stored === '0') {
      return stored === '1';
    }
    if (width < DESKTOP_BREAKPOINT) {
      return true;
    }
    return false;
  };

  const [billingPackage, setBillingPackage] = useState(null);
  const [viewportWidth, setViewportWidth] = useState(getViewportWidth);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(resolveInitialCollapsed);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const isMobile = viewportWidth < MOBILE_BREAKPOINT;
  const isTablet = viewportWidth >= MOBILE_BREAKPOINT && viewportWidth < DESKTOP_BREAKPOINT;

  useEffect(() => {
    if (!authUsername || isSysAdmin) return;
    const token = sessionStorage.getItem('authToken');
    if (!token) return;
    fetch(`${API_BASE_URL}/api/billing/me`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (data?.packageCode) {
          setBillingPackage({
            code: data.packageCode,
            status: data.subscriptionStatus,
            aiEnabled: data.aiEnabled !== false,
            stressTestEnabled: data.stressTestEnabled !== false,
            ifrsEnabled: data.ifrsEnabled === true,
          });
        }
      })
      .catch(() => {});
  }, [authUsername, isSysAdmin]);

  useEffect(() => {
    const handleResize = () => setViewportWidth(getViewportWidth());
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (isMobile) {
      setMobileSidebarOpen(false);
      return;
    }
    const stored = window.localStorage.getItem(SIDEBAR_COLLAPSED_KEY);
    if (stored === '1' || stored === '0') {
      setSidebarCollapsed(stored === '1');
      return;
    }
    setSidebarCollapsed(isTablet);
  }, [isMobile, isTablet]);

  useEffect(() => {
    if (isMobile) {
      return;
    }
    window.localStorage.setItem(SIDEBAR_COLLAPSED_KEY, sidebarCollapsed ? '1' : '0');
  }, [isMobile, sidebarCollapsed]);

  useEffect(() => {
    if (!isMobile) {
      return undefined;
    }
    if (!mobileSidebarOpen) {
      document.body.style.overflow = '';
      return undefined;
    }
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobile, mobileSidebarOpen]);

  useEffect(() => {
    if (isMobile && mobileSidebarOpen) {
      setMobileSidebarOpen(false);
    }
  }, [isMobile, location.pathname, mobileSidebarOpen]);

  const isTenantAdmin = authRole === 'TENANT_ADMIN';
  const visibleNavItems = useMemo(
    () =>
      navItems
        .filter((item) => !(isTenantUser && item.label === 'Sistem Yönetimi'))
        .map((item) => {
          if (!isTenantAdmin || item.label !== 'Sistem Yönetimi' || !item.children) {
            return item;
          }
          const tenantAdminAllowedPaths = new Set([
            '/users',
            '/companies',
            '/periods'
          ]);
          return {
            ...item,
            children: item.children.filter((child) => tenantAdminAllowedPaths.has(child.path))
          }
        }),
    [isTenantUser, isTenantAdmin]
  );
  const packageAwareNavItems = useMemo(
    () =>
      visibleNavItems
        .filter((item) => {
          if (!billingPackage || isSysAdmin) {
            return true;
          }
          if (item.label === 'IFRS-Mali Tablolar') {
            return billingPackage.ifrsEnabled;
          }
          return true;
        })
        .map((item) => {
          if (!billingPackage || item.label !== 'RatioNexus AI' || !item.children) {
            return item;
          }
          return {
            ...item,
            children: item.children.filter((child) => {
              if (child.path === '/analysis/ai-report') {
                return billingPackage.aiEnabled;
              }
              if (child.path === '/analysis/stress-test') {
                return billingPackage.stressTestEnabled;
              }
              return true;
            })
          };
        })
        .filter((item) => !item.children || item.children.length > 0),
    [billingPackage, isSysAdmin, visibleNavItems]
  );
  const [menuSearch, setMenuSearch] = useState('');
  const [openGroups, setOpenGroups] = useState(() => {
    const initial = {};
    packageAwareNavItems.forEach((item) => {
      if (item.children) {
        initial[item.label] = item.children.some((child) =>
          isMenuItemActive(child, location)
        );
      }
    });
    return initial;
  });

  const flatSearchItems = useMemo(() => {
    const rows = [];
    packageAwareNavItems.forEach((item) => {
      if (item.path) {
        rows.push({ label: item.label, path: item.path, groupLabel: '' });
      }
      if (item.children) {
        item.children.forEach((child) => {
          rows.push({
            label: child.label,
            path: child.path,
            groupLabel: item.label
          });
        });
      }
    });
    return rows;
  }, [packageAwareNavItems]);

  const menuSearchResults = useMemo(() => {
    const query = menuSearch.trim().toLowerCase();
    if (!query) {
      return [];
    }
    return flatSearchItems
      .filter((item) => {
        const base = item.label.toLowerCase();
        const translated = menuLabel(item.label).toLowerCase();
        return base.includes(query) || translated.includes(query);
      })
      .slice(0, 8);
  }, [flatSearchItems, menuSearch, menuLabel]);

  useEffect(() => {
    const updates = {};
    packageAwareNavItems.forEach((item) => {
      if (!item.children) {
        return;
      }
      const childActive = item.children.some((child) =>
        isMenuItemActive(child, location)
      );
      if (childActive) {
        updates[item.label] = true;
      }
    });

    if (Object.keys(updates).length > 0) {
      setOpenGroups((prev) => ({ ...prev, ...updates }));
    }
  }, [location, packageAwareNavItems]);

  const handleLogout = () => {
    sessionStorage.removeItem('authToken');
    sessionStorage.removeItem('authRefreshToken');
    sessionStorage.removeItem('authUsername');
    sessionStorage.removeItem('authRole');
    sessionStorage.removeItem('authTenantUserMode');
    navigate('/login', { replace: true });
  };

  const handleSelectSearchResult = (result) => {
    if (result.groupLabel) {
      setOpenGroups((prev) => ({
        ...prev,
        [result.groupLabel]: true
      }));
    }
    navigate(result.path);
    setMenuSearch('');
    if (isMobile) {
      setMobileSidebarOpen(false);
    }
  };

  const handleSidebarToggle = () => {
    if (isMobile) {
      setMobileSidebarOpen((prev) => !prev);
      return;
    }
    setSidebarCollapsed((prev) => !prev);
  };

  const handleNavLinkClick = () => {
    if (isMobile) {
      setMobileSidebarOpen(false);
    }
  };

  return (
    <div className={`layout ${sidebarCollapsed && !isMobile ? 'is-collapsed' : ''} ${isMobile ? 'is-mobile' : ''} ${isMobile && mobileSidebarOpen ? 'is-mobile-open' : ''}`}>
      <aside className="sidebar">
        <div className="brand">
          <img src={APP_LOGO_SRC} alt={t('brand')} className="brand-logo" />
        </div>

        <div className="sidebar-search">
          <div className="sidebar-search-input-wrap">
            <Search size={14} className="sidebar-search-icon" />
            <input
              type="search"
              placeholder={t('menuSearchPlaceholder')}
              value={menuSearch}
              onChange={(event) => setMenuSearch(event.target.value)}
            />
          </div>
          {menuSearchResults.length > 0 && (
            <div className="sidebar-search-results">
              {menuSearchResults.map((result) => (
                <button
                  key={`${result.path}-${result.label}`}
                  type="button"
                  onClick={() => handleSelectSearchResult(result)}
                >
                  <span className="sidebar-text">{menuLabel(result.label)}</span>
                  {result.groupLabel ? <small className="sidebar-text">{menuLabel(result.groupLabel)}</small> : null}
                </button>
              ))}
            </div>
          )}
        </div>

        <nav>
          {packageAwareNavItems.map((item) =>
            item.children ? (
              <div
                key={item.label}
                className={`menu-group ${item.aiGroup ? 'menu-group-ai' : ''} ${openGroups[item.label] ? 'open' : ''}`}
              >
                <button
                  type="button"
                  className={`group-toggle ${
                    item.children.some((child) => isMenuItemActive(child, location))
                      ? 'active'
                      : ''
                  }`}
                  onClick={() =>
                    setOpenGroups((prev) => ({
                      ...prev,
                      [item.label]: !prev[item.label]
                    }))
                  }
                >
                  <span className="icon"><item.icon size={16} /></span>
                  <span className="sidebar-text">{menuLabel(item.label)}</span>
                  <span className="chevron"><ChevronRight size={14} /></span>
                </button>

                {openGroups[item.label] && (
                  <div className="submenu">
                    {item.children.map((child) => (
                      <NavLink
                        key={child.path}
                        to={child.path}
                        onClick={handleNavLinkClick}
                        className={() => (
                          isMenuItemActive(child, location) ? 'nav-link active' : 'nav-link'
                        )}
                      >
                        <span className="icon"><child.icon size={15} /></span>
                        <span className="sidebar-text">{menuLabel(child.label)}</span>
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <NavLink
                key={item.path}
                to={item.path}
                onClick={handleNavLinkClick}
                className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')}
              >
                <span className="icon"><item.icon size={16} /></span>
                <span className="sidebar-text">{menuLabel(item.label)}</span>
              </NavLink>
            )
          )}
        </nav>
      </aside>
      {isMobile ? (
        <button type="button" className="sidebar-backdrop" onClick={() => setMobileSidebarOpen(false)} aria-label="Close menu" />
      ) : null}
      <main className="content">
        <header className="topbar">
          <button
            type="button"
            className="topbar-menu-button"
            onClick={handleSidebarToggle}
            aria-label={isMobile ? 'Toggle menu' : (sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar')}
            title={isMobile ? 'Toggle menu' : (sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar')}
          >
            {isMobile ? <Menu size={16} /> : (sidebarCollapsed ? <PanelLeftOpen size={16} /> : <PanelLeftClose size={16} />)}
          </button>
          {(() => {
            const bc = resolveBreadcrumb(location, packageAwareNavItems);
            return bc ? (
              <div className="topbar-breadcrumb">
                {bc.group ? (
                  <>
                    <span className="topbar-breadcrumb-group">{menuLabel(bc.group)}</span>
                    <span className="topbar-breadcrumb-sep">›</span>
                  </>
                ) : null}
                <strong className="topbar-breadcrumb-page">{menuLabel(bc.page)}</strong>
              </div>
            ) : null;
          })()}
          <div className="topbar-spacer" />
          <div className="topbar-language">
            <label htmlFor="app-language-select">{t('language')}</label>
            <select id="app-language-select" value={language} onChange={(event) => setLanguage(event.target.value)}>
              <option value="tr-TR">{t('turkish')}</option>
              <option value="en-US">{t('english')}</option>
            </select>
          </div>
          <button type="button" className="topbar-notif" aria-label="Bildirimler" title="Bildirimler">
            <Bell size={16} />
          </button>
          {authUsername ? (
            <div className="topbar-user" aria-label={t('topbarUserAria')}>
              <span className="topbar-avatar" aria-hidden="true">
                {authUsername.charAt(0).toUpperCase()}
              </span>
              <div className="topbar-user-info">
                <strong>@{authUsername}</strong>
                {billingPackage && (
                  <span className={`topbar-package-badge topbar-package-badge--${billingPackage.code.toLowerCase()}`}>
                    {PACKAGE_LABELS[billingPackage.code] ?? billingPackage.code}
                  </span>
                )}
              </div>
              <button type="button" className="logout-button topbar-logout" onClick={handleLogout}>
                {t('logout')}
              </button>
            </div>
          ) : null}
        </header>
        <Outlet />
      </main>
    </div>
  );
}

export default AppShell;





